import { z } from 'zod';
export enum ClientKind {
  Api = 'api',
  Bot = 'bot',
  Scheduler = 'scheduler',
  Dashboard = 'dashboard',
  Webview = 'webview',
}
export enum ErrorSeverity {
  Fatal = 'fatal',
  Error = 'error',
  Warn = 'warn',
  Info = 'info',
  Debug = 'debug',
}
const BaseErrorEventSchema = z.object({
  schemaVersion: z.number().int().default(1),
  kind: z.nativeEnum(ClientKind),
  severity: z.nativeEnum(ErrorSeverity),
  message: z
    .string()
    .min(1, 'Error message is required')
    .max(512, 'Error message too long'),
  release: z.string().max(256, 'Release too long'),
  environment: z.string().max(256, 'Environment too long'),
  timestamp: z
    .union([z.date(), z.iso.datetime()])
    .optional()
    .default(() => new Date().toISOString())
    .transform(val => (val instanceof Date ? val.toISOString() : val)),
  stack: z.string().max(16384, 'Stack trace too long').optional(),
  name: z.string().max(256, 'Error name too long').optional(),
  source: z.string().max(500, 'Source too long').optional(),
  lineno: z.number().int().min(0).optional(),
  colno: z.number().int().min(0).optional(),
  userAgent: z.string().max(500, 'User agent too long').optional(),
  url: z.url('Invalid URL format').optional(),
  userId: z.string().max(100, 'User ID too long').optional(),
  sessionId: z.string().max(100, 'Session ID too long').optional(),
  metadata: z.record(z.string(), z.any()).optional(),
});
export const ErrorEventUserSchema = z.object({
  id: z.string().optional(),
  email: z.string().optional(),
});
export const ErrorEventRequestSchema = z.object({
  url: z.string().optional(),
  method: z.string().optional(),
  status: z.number().optional(),
  route: z.string().optional(),
  referrer: z.string().optional(),
});
export const ErrorEventDeviceSchema = z.object({
  userAgent: z.string().optional(),
  platform: z.string().optional(),
});
export const ErrorEventSessionSchema = z.object({
  id: z.string().optional(),
  sequence: z.number().optional(),
  page: z.string().optional(),
});
export const ErrorEventSchemas = {
  full: BaseErrorEventSchema,
  create: BaseErrorEventSchema,
  client: BaseErrorEventSchema.pick({
    schemaVersion: true,
    kind: true,
    severity: true,
    message: true,
    release: true,
    environment: true,
    timestamp: true,
    stack: true,
    name: true,
    url: true,
    userAgent: true,
    userId: true,
    sessionId: true,
    metadata: true,
  }),
};
export const TelemetryHealthResponseSchema = z.object({
  status: z.literal('healthy'),
  enabled: z.boolean(),
  totalEvents: z.number().int().min(0),
});
export const TelemetryErrorCreateResponseSchema = z.object({
  success: z.literal(true),
  message: z.string().optional(),
});
export type ErrorEvent = z.infer<typeof ErrorEventSchemas.full>;
export type CreateErrorEvent = z.infer<typeof ErrorEventSchemas.create>;
export type ClientErrorEvent = z.infer<typeof ErrorEventSchemas.client>;
export type TelemetryHealthResponse = z.infer<
  typeof TelemetryHealthResponseSchema
>;
export type TelemetryErrorCreateResponse = z.infer<
  typeof TelemetryErrorCreateResponseSchema
>;
export type ErrorEventUser = z.infer<typeof ErrorEventUserSchema>;
export type ErrorEventRequest = z.infer<typeof ErrorEventRequestSchema>;
export type ErrorEventDevice = z.infer<typeof ErrorEventDeviceSchema>;
export type ErrorEventSession = z.infer<typeof ErrorEventSessionSchema>;
export const TelemetryRequestSchemas = {
  createError: ErrorEventSchemas.create,
  health: z.object({}),
};
export const TelemetryAPI = {
  createError: {
    request: TelemetryRequestSchemas.createError,
    response: TelemetryErrorCreateResponseSchema,
  },
  health: {
    request: TelemetryRequestSchemas.health,
    response: TelemetryHealthResponseSchema,
  },
};
export type CreateErrorRequest = z.infer<
  typeof TelemetryRequestSchemas.createError
>;
export type HealthRequest = z.infer<typeof TelemetryRequestSchemas.health>;
export type CreateErrorResponse = z.infer<
  typeof TelemetryErrorCreateResponseSchema
>;
export type HealthResponse = z.infer<typeof TelemetryHealthResponseSchema>;
